import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Search, X } from "lucide-react";
import { useTran } from "@/hooks";
import { MultiSelect, SelectField } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { sortReportingYearsDescending } from "../../utils/reporting-years";

const CATEGORY_VALUES = ["Environmental", "Social", "Governance"];
const ALL = "__ALL__";

const TriggerLabel = ({ label, count }) => (
  <span className="inline-flex items-center gap-1.5">
    {label}
    {count > 0 && (
      <Badge
        variant="secondary"
        className="rounded-full bg-blue-100 px-1.5 py-0 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
      >
        {count}
      </Badge>
    )}
  </span>
);

const Chip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[11px] text-foreground">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="text-muted-foreground hover:text-foreground"
      aria-label={`Remove ${label}`}
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

export const DataTableFilterRow = observer(
  ({
    store,
    availableYears,
    reportingYears = [],
    buOptions,
    countryOptions,
    searchValue,
    onSearchChange,
  }) => {
    const { t } = useTran();
    const {
      filters,
      cleared,
      setFilter,
      setCategories,
      clearFilterExplicit,
      clearCategoriesExplicit,
      isMultiSelect,
    } = store;

    const yearItems = useMemo(() => {
      const reportingYearItems = sortReportingYearsDescending(
        reportingYears,
      ).map((reportingYear) => ({
        value: String(reportingYear.startYear),
        label: formatReportingYearPeriod(reportingYear),
      }));

      if (reportingYearItems.length > 0) return reportingYearItems;

      return [...availableYears]
        .sort((a, b) => b - a)
        .map((y) => ({ value: String(y), label: String(y) }));
    }, [availableYears, reportingYears]);
    const buItems = useMemo(
      () => buOptions.map((b) => ({ value: String(b.id), label: b.name })),
      [buOptions],
    );
    const countryItems = useMemo(
      () => countryOptions.map((c) => ({ value: String(c.id), label: c.name })),
      [countryOptions],
    );
    const categoryItems = useMemo(
      () => CATEGORY_VALUES.map((c) => ({ value: c, label: t(c) })),
      [t],
    );

    // For multi fields the items list gets an "All" sentinel prepended.
    // The MultiSelect treats it like any item; the onChange wrapper turns
    // "All clicked" into an array reset and treats empty real value as
    // "All checked" by injecting [ALL] into the displayed value.
    const itemsWithAll = (items) => [{ value: ALL, label: t("All") }, ...items];

    const buildOnChange =
      ({ field, multi, coerce = (v) => v, realValue, wasAllImplicit }) =>
      (next) => {
        if (multi && next.includes(ALL) && realValue.length > 0) {
          setFilter(field, []);
          return;
        }
        if (multi && wasAllImplicit && !next.includes(ALL)) {
          // User unchecked "All" from the all-implicit state — clear
          // everything visually + functionally (no items match).
          clearFilterExplicit(field);
          return;
        }
        const cleaned = next.filter((v) => v !== ALL);
        const trimmed =
          !multi && cleaned.length > 1 ? cleaned.slice(-1) : cleaned;
        setFilter(field, trimmed.map(coerce));
      };

    const triggerSummary =
      ({ label, allLabel, multi, isCleared = false }) =>
      (selected) => {
        if (isCleared) {
          return <TriggerLabel label={t("None selected")} count={0} />;
        }
        if (allLabel && selected.some((o) => o?.value === ALL)) {
          return <TriggerLabel label={allLabel} count={0} />;
        }
        const realSelected = selected.filter((o) => o.value !== ALL);
        if (!multi && realSelected.length === 1) {
          return <span>{realSelected[0].label}</span>;
        }
        return <TriggerLabel label={label} count={realSelected.length} />;
      };

    const renderPicker = ({
      field,
      items,
      realValue,
      placeholder,
      allLabel,
      searchPlaceholder,
      className,
      coerce = (v) => v,
      stringify = (v) => String(v),
    }) => {
      const multi = isMultiSelect(field);
      if (!multi) {
        const currentValue =
          realValue.length > 0 ? stringify(realValue[0]) : "";
        return (
          <SelectField
            options={items}
            value={currentValue}
            onValueChange={(v) => setFilter(field, v ? [coerce(v)] : [])}
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
            selectClassName={className}
            sortOptions={false}
          />
        );
      }
      const isCleared = Boolean(cleared?.[field]);
      const wasAllImplicit = realValue.length === 0 && !isCleared;
      const displayValue = isCleared
        ? []
        : wasAllImplicit
          ? [ALL, ...items.map((i) => String(i.value))]
          : realValue.map(stringify);
      return (
        <MultiSelect
          options={itemsWithAll(items)}
          value={displayValue}
          onChange={buildOnChange({
            field,
            multi,
            coerce,
            realValue,
            wasAllImplicit,
          })}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          className={className}
          selectedSummary={triggerSummary({
            label: placeholder,
            allLabel,
            multi,
            isCleared,
          })}
        />
      );
    };

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("Filters")}
        </span>
        {renderPicker({
          field: "years",
          items: yearItems,
          realValue: filters.years,
          placeholder: t("Year"),
          allLabel: t("All Years"),
          searchPlaceholder: t("Search year..."),
          className: "h-9 w-[240px]",
          coerce: Number,
        })}
        {renderPicker({
          field: "bus",
          items: buItems,
          realValue: filters.bus,
          placeholder: t("Reporting Entity"),
          allLabel: t("All Reporting Entities"),
          searchPlaceholder: t("Search Reporting Entities..."),
          className: "h-9 w-[180px]",
        })}
        {renderPicker({
          field: "countries",
          items: countryItems,
          realValue: filters.countries,
          placeholder: t("Country"),
          allLabel: t("All Countries"),
          searchPlaceholder: t("Search country..."),
          className: "h-9 w-[180px]",
        })}
        {(() => {
          const isCleared = Boolean(cleared?.categories);
          const wasAllImplicit = filters.categories.length === 0 && !isCleared;
          const catItemsWithAll = itemsWithAll(categoryItems);
          const displayValue = isCleared
            ? []
            : wasAllImplicit
              ? [ALL, ...categoryItems.map((i) => String(i.value))]
              : filters.categories.map(String);
          return (
            <MultiSelect
              options={catItemsWithAll}
              value={displayValue}
              onChange={(next) => {
                if (next.includes(ALL) && filters.categories.length > 0) {
                  setCategories([]);
                  return;
                }
                if (wasAllImplicit && !next.includes(ALL)) {
                  clearCategoriesExplicit();
                  return;
                }
                setCategories(next.filter((v) => v !== ALL));
              }}
              placeholder={t("Category")}
              searchPlaceholder={t("Search category...")}
              className="h-9 w-[160px]"
              selectedSummary={triggerSummary({
                label: t("Category"),
                allLabel: t("All Categories"),
                multi: true,
                isCleared,
              })}
            />
          );
        })()}
        {onSearchChange && (
          <div className="relative w-48 sm:w-56">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchValue ?? ""}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("Search metric...")}
              className="h-9 pl-7 text-xs"
            />
          </div>
        )}
      </div>
    );
  },
);

export const DataTableFilterChips = observer(
  ({ store, buOptions, countryOptions, reportingYears = [] }) => {
    const { t } = useTran();
    const { filters, setFilter, setCategories, clearFilters } = store;

    const removeFromMutex = (field, value, coerce = (v) => v) => {
      const next = filters[field].filter((v) => coerce(v) !== coerce(value));
      setFilter(field, next);
    };
    const removeCategory = (value) =>
      setCategories(filters.categories.filter((c) => c !== value));
    const reportingYearLabelByYear = new Map(
      reportingYears.map((reportingYear) => [
        Number(reportingYear.startYear),
        formatReportingYearPeriod(reportingYear),
      ]),
    );

    const chips = [
      ...filters.years.map((y) => ({
        key: `year:${y}`,
        label: reportingYearLabelByYear.get(Number(y)) ?? String(y),
        onRemove: () => removeFromMutex("years", y, Number),
      })),
      ...filters.bus.map((id) => {
        const m = buOptions.find((o) => String(o.id) === String(id));
        return {
          key: `bu:${id}`,
          label: m?.name ?? String(id),
          onRemove: () => removeFromMutex("bus", id),
        };
      }),
      ...filters.countries.map((id) => {
        const m = countryOptions.find((o) => String(o.id) === String(id));
        return {
          key: `country:${id}`,
          label: m?.name ?? String(id),
          onRemove: () => removeFromMutex("countries", id),
        };
      }),
      ...filters.categories.map((c) => ({
        key: `cat:${c}`,
        label: t(c),
        onRemove: () => removeCategory(c),
      })),
    ];

    if (chips.length === 0) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map((c) => (
          <Chip key={c.key} label={c.label} onRemove={c.onRemove} />
        ))}
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60"
        >
          <X className="h-3 w-3" />
          {t("Clear all")}
        </button>
      </div>
    );
  },
);
